import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, LogOut, Users, Eye, MousePointerClick, Send, Clock, Radio, ExternalLink, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { analyticsSummary, analyticsRealtime } from "@/lib/api";

const RANGES = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["365d", "1 an"], ["all", "Tout"]];
const COLORS = ["#3E9BFF", "#5CB0FF", "#7Fc0ff", "#2b7fe0", "#9ecbff", "#1f6bc0"];

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5">
    <div className="flex items-center gap-2 text-secondary2 mb-3"><Icon size={15} className="text-electric" /><span className="text-[10px] tracking-[0.2em] uppercase">{label}</span></div>
    <div className="font-serif text-4xl text-white">{value}</div>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5">
    <div className="text-[11px] tracking-[0.25em] uppercase text-electric mb-4">{title}</div>
    {children}
  </div>
);

const ListTable = ({ rows, unit }) => (
  <div className="space-y-2">
    {(!rows || rows.length === 0) && <p className="text-secondary2 text-sm">Aucune donnée.</p>}
    {rows?.map((r, i) => (
      <div key={i} className="flex items-center justify-between text-sm">
        <span className="text-white/80 truncate pr-3">{r.name}</span>
        <span className="text-electric tabular-nums">{r.value}{unit || ""}</span>
      </div>
    ))}
  </div>
);

export default function AdminAnalytics() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [rt, setRt] = useState({ active: 0, recent: [] });

  const load = useCallback(async () => { setData(await analyticsSummary(range)); }, [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tick = async () => { try { setRt(await analyticsRealtime()); } catch (e) {} };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans">
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/karams-logo.png" alt="Karams" className="h-9 w-auto" />
            <nav className="hidden sm:flex items-center gap-1 text-xs">
              <Link to="/admin" className="px-3 py-1.5 rounded text-secondary2 hover:text-white flex items-center gap-2"><LayoutDashboard size={14} /> Contenu</Link>
              <span className="px-3 py-1.5 rounded bg-surface text-electric">Statistiques</span>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric"><ExternalLink size={14} /> Voir le site</a>
            <button onClick={doLogout} className="text-white/60 hover:text-white"><LogOut size={16} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl">Statistiques</h1>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-electric/40 text-electric text-xs"><Radio size={13} className="animate-pulse" /> {rt.active} en direct</span>
          </div>
          <div className="flex gap-1 bg-[#0a0a0a] border border-white/10 rounded-full p-1">
            {RANGES.map(([k, l]) => (
              <button key={k} data-testid={`analytics-range-${k}`} onClick={() => setRange(k)}
                className={`px-4 py-1.5 rounded-full text-xs transition-colors ${range === k ? "bg-electric text-black" : "text-secondary2 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>

        {!data ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-electric" /></div> : (
          <div className="space-y-6" data-testid="analytics-content">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Stat icon={Users} label="Visiteurs" value={data.totals.visitors} />
              <Stat icon={Eye} label="Pages vues" value={data.totals.pageviews} />
              <Stat icon={Radio} label="En direct" value={data.totals.realtime} />
              <Stat icon={Clock} label="Temps moyen" value={`${data.totals.avgDuration}s`} />
              <Stat icon={MousePointerClick} label="Clics boutons" value={data.totals.clicks} />
              <Stat icon={Send} label="Formulaires" value={data.totals.forms} />
            </div>

            <Panel title="Évolution du trafic">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8 }} labelStyle={{ color: "#fff" }} />
                  <Line type="monotone" dataKey="views" name="Pages vues" stroke="#3E9BFF" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="visitors" name="Visiteurs" stroke="#5CB0FF" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel title="Provenance des visiteurs">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={data.referrers} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                      {data.referrers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Appareils">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.devices}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                    <XAxis dataKey="name" stroke="#666" fontSize={11} />
                    <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8 }} cursor={{ fill: "#ffffff08" }} />
                    <Bar dataKey="value" name="Sessions" fill="#3E9BFF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Panel title="Pages les plus vues"><ListTable rows={data.topPages} /></Panel>
              <Panel title="Pays"><ListTable rows={data.countries} /></Panel>
              <Panel title="Villes"><ListTable rows={data.cities} /></Panel>
              <Panel title="Clics par bouton"><ListTable rows={data.buttonClicks} /></Panel>
              <Panel title="Sources détaillées"><ListTable rows={data.referrers} /></Panel>
              <Panel title="Activité temps réel (5 min)">
                <ListTable rows={rt.recent.map((r) => ({ name: `${r.type} · ${r.country} · ${r.device}`, value: r.source }))} />
              </Panel>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
